import LinearProgress from '@mui/material/LinearProgress';
import { appRoutes } from 'app/app-routes';
import { formValidationSchema } from 'common/functions/form-validation-schema';
import wsCookies from 'common/tools/ws-cookies';
import { UserActionCodeValues } from 'common/types/user-action-code';
import { Formik } from 'formik';
import { UserActionCode, useUsersLoginMutation } from 'graphql/generated';
import __ from 'react';
import { useDispatch } from 'react-redux';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { cacheActions } from 'redux/cache.slice';
import { WsFormCheckbox } from 'stories/forms/WsFormCheckbox';
import { WsFormTextField } from 'stories/forms/WsFormTextField';
import { WsButton } from 'stories/inputs/WsButton';
import WsFlexbox from 'stories/layout/WsFlexbox';
import WsFormBox from 'stories/layout/WsFormBox';
import WsPanel from 'stories/layout/WsPanel';
import * as yup from 'yup';

interface Props {
  noRoute?: boolean;
}

interface FormValues {
  email: string;
  password: string;
  stayLoggedIn: boolean;
}

const initialValues: FormValues = {
  email: '',
  password: '',
  stayLoggedIn: false,
};

const validationSchema = yup.object().shape({
  email: formValidationSchema.email,
  password: formValidationSchema.required,
});

type LocationState = {
  from: {
    pathname: string;
  };
};

const LoginWidget = (props: Props) => {
  const dispatch = useDispatch();
  const [login] = useUsersLoginMutation();
  const navigate = useNavigate();
  const location = useLocation();
  const sourcePath = location?.state ? (location?.state as LocationState).from.pathname : '';

  const formAction = async (formValues: FormValues, setSubmitting: (isSubmitting: boolean) => void) => {
    try {
      setSubmitting(true);
      const { data } = await login({
        variables: { email: formValues.email, password: formValues.password },
      });
      setSubmitting(false);
      if (!data) throw new Error('Register user error');
      const result = data.usersLogin;
      if (result.code === UserActionCode.loggedIn && result.user) {
        const { __typename, ...user } = result.user;
        dispatch(cacheActions.user(user));
        if (formValues.stayLoggedIn) {
          wsCookies.set('userId', user.id);
        }
        if (sourcePath) navigate(sourcePath, { replace: true });
        else navigate(-1);
        dispatch(
          cacheActions.addSnack({
            message: UserActionCodeValues[result.code],
            variant: 'success',
          }),
        );
        if (!result.user?.verified)
          setTimeout(() => {
            dispatch(
              cacheActions.addSnack({
                message: 'Your email is not verified.',
                variant: 'warning',
                persist: true,
                actionText: 'Verify Now',
                actionLink: `${appRoutes.verifyEmail}/${result.user?.email}`,
              }),
            );
          }, 3000);
      } else throw new Error(UserActionCodeValues[result.code]);
    } catch (error: any) {
      setSubmitting(false);
      dispatch(
        cacheActions.addSnack({
          message: error.message,
          variant: 'error',
        }),
      );
    }
  };

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialValues}
      onSubmit={(values, { setSubmitting }) => formAction(values, setSubmitting)}
    >
      {props => (
        <WsPanel width="360px" title="Log In to your Account">
          <form onSubmit={props.handleSubmit}>
            <WsFormBox>
              <WsFormTextField id="email" label="Email" type="email" fullWidth formik={props} />
              <WsFormTextField id="password" label="Password" type="password" fullWidth formik={props} />
              <WsFlexbox centered sx={{ padding: 0 }}>
                <WsFormCheckbox id="stayLoggedIn" label="Stay Logged In" formik={props} />
              </WsFlexbox>
              {props.isSubmitting && <LinearProgress />}
              <WsButton label="Log In" type="submit" disabled={props.isSubmitting} />
              <WsFlexbox centered>
                <NavLink to={`${appRoutes.resetPassword}/${props.values.email}`}>Forgot your password?</NavLink>
              </WsFlexbox>
              <WsFlexbox centered>
                <NavLink to={appRoutes.register}>Create an account</NavLink>
              </WsFlexbox>
            </WsFormBox>
          </form>
        </WsPanel>
      )}
    </Formik>
  );
};

export default LoginWidget;
